/**
 * cart-points.js
 * Loyalty points redemption for the cart page.
 *
 * Exposed as:  window.CartPoints
 *
 * Flow:
 *   Page load : get-member-point-voucher-timer → restore UI or silently clean up
 *   Apply     : unlock existing (if any) → lock new tier → fetchTimer → refreshCart
 *   Remove    : unlock → fetchTimer → refreshCart
 *   Expiry    : removeApplied(true) — silent, cart is about to be cleared
 *
 * Points operations are fully independent from coupons.
 * Redemption rate is configurable via shop metafield custom.stars_per_dollar
 * (integer; default 10 = 10 stars/$1), and the redeemable amount is capped by BOTH
 * the member's star balance and the redeemable base minus settings.cart_min_payable_amount
 * (the merchandise total can never be driven below the minimum payable amount).
 * Shopify subtotal is re-fetched from /cart.js after every operation via
 * CartUtils.refreshCart() — no client-side discount math.
 *
 * Depends on:  window.CartLockManager (cart-lock-manager.js)
 *              window.CartTimer       (cart-timer.js)
 *              window.CartUtils       (cart-timer.js)
 *              window.CartCoupon      (cart-coupon-drawer.js) — only for restoreFromCodes on page load
 */

(function (global) {
  'use strict';

  // Rate: _starsPerDollar stars = $1 (pts / rate) for all currencies.
  // $-input steps by 1, so a valid tier is a multiple of rate stars ($1 min).
  function _tierFor(pts) {
    pts = parseInt(pts, 10);
    var minPts = _starsPerDollar;
    if (!pts || pts < minPts || pts % minPts !== 0) return null;
    return { points: pts, discount: pts / _starsPerDollar };
  }

  // Redeemable base (major units, e.g. 100 for RM100):
  //   base = (cart.total_price + POINTS allocation) − Σ final_line_price of redeemed lines
  //
  //   + cart.total_price already nets out coupons and every other order-level
  //     discount, so stars can never be stacked on top of a coupon past $0.
  //   + the POINTS allocation is added back so the base is invariant under the
  //     points lock itself — otherwise _reconcilePoints() oscillates
  //     (apply max → total $0 → cap $0 → unlock → total back up → re-apply → …).
  //   − redeemed gift lines are not payable merchandise. Same convention as the
  //     free-shipping bar. A gift-only cart therefore yields base 0.
  function _redeemableBaseDollars() {
    var el = document.querySelector('[data-cart-subtotal]');
    if (!el) return 0;

    var total    = parseInt(el.getAttribute('data-cart-total-cents')     || '0', 10);
    var redeemed = parseInt(el.getAttribute('data-cart-redeemed-cents')  || '0', 10);

    // Server-reported allocation is preferred (it handles partial applicability).
    // Fall back to the locked tier when the attribute is missing (stale markup).
    var allocAttr = el.getAttribute('data-cart-points-alloc-cents');
    var pointsAll = allocAttr === null
      ? (_applied ? _applied.discount * 100 : 0)
      : parseInt(allocAttr || '0', 10);

    if (isNaN(total))     total     = 0;
    if (isNaN(redeemed))  redeemed  = 0;
    if (isNaN(pointsAll)) pointsAll = 0;

    var cents = (total + pointsAll) - redeemed;
    return cents > 0 ? Math.floor(cents / 100) : 0;
  }

  // Max redeemable cash = min(balance in $, base − minPayable), in whole $1 units.
  function _computeMaxCash() {
    var starCash = Math.floor(_availablePoints / _starsPerDollar);  // $ the star balance covers
    var payable  = _redeemableBaseDollars() - _minPayable;          // $ redeemable without hitting the floor
    return Math.max(0, Math.min(starCash, payable));
  }

  // Which "can't redeem" state to show when the cap is below $1:
  // an empty wallet and an order that's too small are different problems.
  function _showIneligibleState() {
    var starCash = Math.floor(_availablePoints / _starsPerDollar);
    var el = starCash < 1
      ? _wrapEl('[data-points-insufficient]')
      : (_wrapEl('[data-points-order-too-small]') || _wrapEl('[data-points-insufficient]'));
    _show(el);
  }

  var _CURRENCY_SYMBOLS = {
    HKD: 'HK$', CNY: 'CNY', MOP: 'MOP$',
    SGD: 'SGD', MYR: 'RM',  TWD: 'TWD',
  };

  var _customerId      = null;
  var _membershipId    = null;
  var _availablePoints = 0;
  var _currencySymbol  = 'RM';
  var _starsPerDollar  = parseInt(document.querySelector("#cart-order-summary")?.dataset.starsPerDollar || '10');   // from shop metafield custom.stars_per_dollar; 10 = fallback
  var _minPayable      = 0;   // settings.cart_min_payable_amount, major units; 0 = allow $0 orders

  var _applied      = null;   // { points, discount } or null
  var _isLocking    = false;
  var _eventsBound  = false;
  var _reapplyTimer = null;   // debounce handle for cart-change reconcile

  var _wrap = null;

  /* ─── Localized strings (from [data-points-redemption] data-attrs; English defaults) ─ */
  var _STR = { starsCount: '[count] Stars', removeStars: 'Remove stars' };
  function _loadStrings() {
    var w = document.querySelector('[data-points-redemption]');
    if (!w) return;
    if (w.dataset.strStarsCount)  _STR.starsCount  = w.dataset.strStarsCount;
    if (w.dataset.strRemoveStars) _STR.removeStars = w.dataset.strRemoveStars;
  }

  function _el(sel)     { return document.querySelector(sel); }
  function _wrapEl(sel) { return _wrap ? _wrap.querySelector(sel) : null; }

  /* ─── Whole-cart-page busy overlay ───────────────────────────────────────── */
  // Toggles the dim overlay + spinner (markup/CSS in cart-layout.liquid) while a
  // points lock/unlock round-trip is in flight, so the cart doesn't sit with
  // stale totals and the user can't fire a second operation mid-flight.
  function _setCartBusy(on) {
    var layout = document.querySelector('.cart-page-layout');
    if (layout) layout.classList.toggle('is-busy', !!on);
  }

  /* ─── Setup number input ─────────────────────────────────────────────────── */
  function _setup(points) {
    _wrap = _el('[data-points-redemption]');
    if (!_wrap) return;
    _loadStrings();

    _availablePoints = points || 0;

    if (points === 0) {
      _show(_wrapEl('[data-points-empty]'));
      return;
    }

    var maxCash = _computeMaxCash();
    if (maxCash < 1) {
      _showIneligibleState();
      return;
    }

    var maxLabel  = _wrapEl('[data-points-max-label]');
    var symEl     = _wrapEl('[data-points-currency-symbol]');
    if (maxLabel) maxLabel.textContent = _currencySymbol + maxCash;
    if (symEl)    symEl.textContent    = _currencySymbol;

    var input = _wrapEl('[data-points-input]');
    if (input) {
      input.max   = String(maxCash);
      input.value = '0';
    }

    _updateStarsSummary(0);
    _show(_wrapEl('[data-points-controls]'));
    _bindEvents();
  }

  /* ─── Events ─────────────────────────────────────────────────────────────── */
  function _bindEvents() {
    if (_eventsBound) return;
    _eventsBound = true;

    var input    = _wrapEl('[data-points-input]');
    var upBtn    = _wrapEl('[data-points-up]');
    var downBtn  = _wrapEl('[data-points-down]');
    var applyBtn = _wrapEl('[data-points-apply]');
    if (input) {
      input.addEventListener('input', function () {
        _updateStarsSummary(_clampValue(parseFloat(input.value) || 0));
      });
      input.addEventListener('blur', function () {
        var val = _clampValue(parseFloat(input.value) || 0);
        input.value = String(val);
        _updateStarsSummary(val);
      });
    }
    if (upBtn) {
      upBtn.addEventListener('mousedown', function (e) { e.preventDefault(); });
      upBtn.addEventListener('click', function () { _stepInput(1); });
    }
    if (downBtn) {
      downBtn.addEventListener('mousedown', function (e) { e.preventDefault(); });
      downBtn.addEventListener('click', function () { _stepInput(-1); });
    }
  }

  function _clampValue(val) {
    var input   = _wrapEl('[data-points-input]');
    var maxCash = input ? parseInt(input.max, 10) : 0;
    val = Math.round(val);
    if (val < 0) val = 0;   // 0 = redeem nothing (opt-in)
    if (maxCash && val > maxCash) val = maxCash;
    return val;
  }

  function _stepInput(delta) {
    var input = _wrapEl('[data-points-input]');
    if (!input) return;
    var next = _clampValue((parseFloat(input.value) || 0) + delta);
    input.value = String(next);
    _updateStarsSummary(next);
  }

  /* ─── Stars summary ─────────────────────────────────────────────────────── */
  function _updateStarsSummary(cashAmount) {
    var ptsRequired  = cashAmount * _starsPerDollar;
    var ptsRemaining = _availablePoints - ptsRequired;

    var elAvail     = _wrapEl('[data-points-stars-available]');
    var elRequired  = _wrapEl('[data-points-stars-required]');
    var elRemaining = _wrapEl('[data-points-stars-remaining]');

    if (elAvail)     elAvail.textContent     = _availablePoints.toLocaleString();
    if (elRequired)  elRequired.textContent  = ptsRequired.toLocaleString();
    if (elRemaining) elRemaining.textContent = ptsRemaining.toLocaleString();
  }

  /* ─── Render points tag in [data-coupon-tags] ───────────────────────────── */
  function _renderPointsTag(tier) {
    var container = _el('[data-coupon-tags]');
    if (!container) return;
    var existing = container.querySelector('[data-points-tag]');
    if (existing) existing.remove();
    var tag = document.createElement('div');
    tag.className = 'cart-applied-tag';
    tag.setAttribute('data-points-tag', '');
    tag.innerHTML =
      '<p class="p3">' + _STR.starsCount.replace('[count]', tier.points) + '</p>' +
      '<button type="button" class="cart-applied-tag__remove" aria-label="' + _STR.removeStars + '">&#215;</button>';
    tag.querySelector('button').addEventListener('click', function () { removeApplied(false); });
    container.appendChild(tag);
  }

  /* ─── Apply tier to UI (no API) ──────────────────────────────────────────── */
  function _applyTierUI(tier) {
    _applied = tier;

    _renderPointsTag(tier);

    // Reflect the applied redemption in the input + stars summary so a page
    // refresh (or coupon-triggered re-sync) keeps Stars Available / Required /
    // Remaining showing the redemption that's actually locked in the cart,
    // instead of resetting to 0. tier.discount is the cash value (pts / 10).
    var input = _wrapEl('[data-points-input]');
    if (input) input.value = String(tier.discount);
    _updateStarsSummary(tier.discount);

    // [data-points-discount-row] removed — points discount is now a real Shopify
    // discount code applied via syncShopifyDiscounts(); it shows in [data-discount-rows]
    // alongside coupon discounts and is already included in cart.total_price.
    // var lbl = _el('[data-points-discount-label]');
    // var amt = _el('[data-points-discount-amount]');
    // var row = _el('[data-points-discount-row]');
    // if (lbl) lbl.textContent = 'Points Redemption (' + tier.points + ')';
    // if (amt) amt.textContent = '- RM' + tier.discount + '.00';
    // if (row) row.style.display = 'flex';
  }

  /* ─── Sync coupon display from timer response ───────────────────────────── */
  // Backend may auto-lock/unlock coupons when points are locked/unlocked.
  // Always call this after fetchTimer() in the points flow.
  function _syncCouponsFromTimer(timerRes) {
    if (global.CartCoupon && timerRes && timerRes.data) {
      global.CartCoupon.restoreFromCodes(timerRes.data.coupon_codes || []);
    }
  }

  /* ─── Points coupon code helper ─────────────────────────────────────────── */
  // get-member-point-voucher-timer does not return points_coupon_code separately.
  // Derive it by finding the code starting with 'POINTS' in the coupon_codes list.
  // There should be at most one such code (one active points lock at a time).
  function _findPointsCouponCode(codes) {
    return (codes && codes.find(function (c) { return c.indexOf('POINTS') === 0; })) || null;
  }

  /**
   * Sync points state from a get-member-point-voucher-timer response.
   * Called by cart-coupon-drawer.js after coupon operations, because removing a
   * points-generated coupon causes the backend to auto-unlock the points too.
   * Also re-seeds CartLockManager so unlockPoints has the correct payload later.
   */
  function syncFromTimer(timerRes) {
    if (!timerRes || !timerRes.data) return;
    var serverPoints = timerRes.data.points ? parseInt(timerRes.data.points, 10) : 0;

    if (!serverPoints) {
      // Backend auto-unlocked points
      if (_applied) {
        _applied = null;
        _clearPointsUI();
        // recalcTotalSavings will be called by refreshCart() in the coupon flow
      }
      if (global.CartLockManager) global.CartLockManager.seedPointsLock(null, null);
    } else if (!_applied || _applied.points !== serverPoints) {
      // Points still locked, possibly a different tier — restore display
      var tier = _tierFor(serverPoints);
      if (tier) _applyTierUI(tier);
      // Re-seed so unlockPoints has the current discount code.
      // Derive the points coupon code from coupon_codes (starts with 'POINTS').
      if (global.CartLockManager) {
        global.CartLockManager.seedPointsLock(
          serverPoints,
          _findPointsCouponCode(timerRes.data.coupon_codes || [])
        );
      }
    }
  }

  /* ─── Clear points UI (no API) ───────────────────────────────────────────── */
  function _clearPointsUI() {
    var existingTag = _el('[data-points-tag]');
    if (existingTag) existingTag.remove();
    var input = _wrapEl('[data-points-input]');
    if (input) {
      input.value = '0';
      _updateStarsSummary(0);
    }
  }

  /* ─── Apply points (auto on blur / stepper) ─────────────────────────────── */
  async function _applyPoints() {
    if (_isLocking) return;

    var input = _wrapEl('[data-points-input]');
    var cashVal = input ? parseFloat(input.value) : 0;
    var pts = cashVal * _starsPerDollar;
    var tier = _tierFor(pts);

    if (!tier) {
      if (_applied) await removeApplied(false);
      return;
    }
    if (_applied && _applied.points === tier.points) return;

    var upBtn    = _wrapEl('[data-points-up]');
    var downBtn  = _wrapEl('[data-points-down]');
    var applyBtn = _wrapEl('[data-points-apply]');
    var applyTxt = applyBtn ? applyBtn.textContent : '';
    if (input)    { input.disabled    = true; }
    if (upBtn)    { upBtn.disabled    = true; }
    if (downBtn)  { downBtn.disabled  = true; }
    if (applyBtn) { applyBtn.disabled = true; applyBtn.classList.add('is-loading'); }

    _isLocking = true;
    _setCartBusy(true);
    try {
      if (_applied) {
        await global.CartLockManager.unlockPoints();
        _applied = null;
      }

      var ok = await global.CartLockManager.lockPoints(tier);
      if (!ok) {
        console.error('[CartPoints] lockPoints failed for tier', tier.points);
        return;
      }

      _applyTierUI(tier);
      var timerRes = await global.CartLockManager.fetchTimer();
      _syncCouponsFromTimer(timerRes);
      if (global.CartUtils) await global.CartUtils.refreshCart();
      document.dispatchEvent(new CustomEvent('cart:points-applied', { detail: tier }));
    } finally {
      _isLocking = false;
      _setCartBusy(false);
      if (input)    { input.disabled    = false; }
      if (upBtn)    { upBtn.disabled    = false; }
      if (downBtn)  { downBtn.disabled  = false; }
      if (applyBtn) { applyBtn.disabled = false; applyBtn.classList.remove('is-loading'); }
    }
  }

  /* ─── Remove applied points ──────────────────────────────────────────────── */
  // silent=true: called by CartTimer on expiry — skip fetchTimer/refreshCart.
  // silent=false (default): user-triggered — full refresh.
  async function removeApplied(silent) {
    if (!_applied) return;

    // User-triggered (tag × / qty-drop): show the cart-page overlay and mark the
    // tag itself as loading so it's clear the removal is in flight. Silent =
    // timer expiry, cart about to reload — skip all feedback + network.
    var tag = _el('[data-points-tag]');
    if (!silent) {
      _setCartBusy(true);
      if (tag) {
        tag.classList.add('is-loading');
        var rmBtn = tag.querySelector('button');
        if (rmBtn) rmBtn.disabled = true;
      }
      // Update Stars Available / Required / Remaining immediately (before the
      // network round-trip finishes) so the summary reflects "nothing redeemed"
      // the moment removal starts and is already correct when the loader hides.
      var summaryInput = _wrapEl('[data-points-input]');
      if (summaryInput) summaryInput.value = '0';
      _updateStarsSummary(0);
    }

    try {
      await global.CartLockManager.unlockPoints();
      _applied = null;
      if (!silent) {
        var timerRes = await global.CartLockManager.fetchTimer();
        _syncCouponsFromTimer(timerRes);
        if (global.CartUtils) await global.CartUtils.refreshCart();
      }
      _clearPointsUI();
      document.dispatchEvent(new CustomEvent('cart:points-removed'));
    } finally {
      if (!silent) _setCartBusy(false);
    }
  }

  /* ─── Recompute cap when the cart changes ────────────────────────────────── */
  // The redeemable max depends on the order value, so re-run it whenever cart
  // items change (cart-items.js dispatches cart:updated after updating the
  // subtotal data-attrs). Converges: any re-apply/remove leaves _applied at the
  // new cap, so the follow-up cart:updated is a no-op.
  function _onCartUpdated() {
    if (!_wrap || !_availablePoints) return;
    var controls = _wrapEl('[data-points-controls]');
    if (!controls) return;

    var maxCash = _computeMaxCash();
    var input   = _wrapEl('[data-points-input]');

    // Order (or balance) can no longer cover the $1 minimum → drop + show why.
    if (maxCash < 1) {
      _showIneligibleState();
      if (_applied) _scheduleReconcile();   // remove the lock (debounced)
      return;
    }

    // Immediate, no-network UI sync — cheap and safe to run on every event.
    var maxLabel = _wrapEl('[data-points-max-label]');
    if (maxLabel) maxLabel.textContent = _currencySymbol + maxCash;
    if (input)    input.max = String(maxCash);
    _show(controls);

    // Applied amount now exceeds the order cap → reflect the reduced amount
    // instantly and schedule a debounced re-lock (the actual network work).
    if (_applied && _applied.points > maxCash * _starsPerDollar) {
      if (input) {
        input.value = String(maxCash);
        _updateStarsSummary(maxCash);
      }
      _scheduleReconcile();
      return;
    }

    // Otherwise just clamp the visible input to the new max.
    if (input) {
      var clamped = _clampValue(parseFloat(input.value) || 0);
      input.value = String(clamped);
      _updateStarsSummary(clamped);
    }
  }

  // Debounce the network reconcile so a burst of qty changes — and the duplicate
  // cart:updated that CartItems' own /cart.js reload dispatches — collapse into a
  // SINGLE unlock/lock cycle instead of one round-trip per event.
  function _scheduleReconcile() {
    if (_reapplyTimer) clearTimeout(_reapplyTimer);
    _reapplyTimer = setTimeout(_reconcilePoints, 350);
  }

  async function _reconcilePoints() {
    _reapplyTimer = null;
    if (_isLocking) { _scheduleReconcile(); return; }   // a lock is mid-flight — retry after it settles
    if (!_availablePoints || !_applied) return;

    var maxCash = _computeMaxCash();
    if (maxCash < 1) {
      await removeApplied(false);
      return;
    }
    if (_applied.points > maxCash * _starsPerDollar) {
      var input = _wrapEl('[data-points-input]');
      if (input) input.value = String(maxCash);
      await _applyPoints();
    }
  }

  /* ─── Helpers ────────────────────────────────────────────────────────────── */
  function _show(el) {
    if (!_wrap) return;
    [
      _wrapEl('[data-points-loading]'),
      _wrapEl('[data-points-empty]'),
      _wrapEl('[data-points-insufficient]'),
      _wrapEl('[data-points-order-too-small]'),
      _wrapEl('[data-points-controls]'),
    ].forEach(function (e) { if (e) e.style.display = 'none'; });
    if (el) el.style.display = 'block';
  }

  /* ─── Init ───────────────────────────────────────────────────────────────── */
  async function _init() {
    _wrap = _el('[data-points-redemption]');
    _loadStrings();

    var ctxEl = document.querySelector('[data-loyalty-customer-id]');
    if (ctxEl) {
      _customerId   = ctxEl.getAttribute('data-loyalty-customer-id') || null;
      _membershipId = ctxEl.getAttribute('data-loyalty-membership-id') || null;
    }

    // Guest: hide loading shimmer, nothing else to do (member-only features)
    if (!_customerId) {
      if (_wrap) _show(_wrapEl('[data-points-empty]'));
      return;
    }

    // Init shared lock manager with customer context
    if (global.CartLockManager) {
      global.CartLockManager.init(_customerId, _membershipId);
    }

    var currencyEl = document.querySelector('[data-points-currency]');
    var currency   = currencyEl ? currencyEl.getAttribute('data-points-currency') : 'RM';
    _currencySymbol = _CURRENCY_SYMBOLS[currency] || currency;

    // Redemption rate from shop metafield custom.stars_per_dollar (integer); 10 = fallback
    var rate = parseInt(ctxEl.getAttribute('data-stars-per-dollar'), 10);
    if (!isNaN(rate) && rate > 0) _starsPerDollar = rate;

    // Minimum payable merchandise total from settings.cart_min_payable_amount; 0 = off
    var minPayable = parseFloat(ctxEl.getAttribute('data-min-payable'));
    if (!isNaN(minPayable) && minPayable > 0) _minPayable = minPayable;

    var points = parseInt(ctxEl.getAttribute('data-loyalty-points'), 10);
    _setup(isNaN(points) ? 0 : points);

    // Fetch active lock state from server
    console.log('[CartPoints] → get-member-point-voucher-timer');
    var body = new FormData();
    body.append('action', 'get-member-point-voucher-timer');
    body.append('customerId', _customerId);
    var timerRes = null;
    try {
      var res  = await fetch('/apps/integration', { method: 'POST', body: body });
      var text = await res.text();
      if (res.ok && text.trim()) timerRes = JSON.parse(text);
      console.log('[CartPoints] ← get-member-point-voucher-timer', timerRes);
    } catch (e) {
      console.error('[CartPoints] get-member-point-voucher-timer failed:', e);
    }

    if (!timerRes || timerRes.status !== 'success' || !timerRes.data) return;

    var data = timerRes.data;

    if (!data.time_applied) {
      // No active lock — clear any stale Shopify discount codes left from a previous session
      if (global.CartLockManager) await global.CartLockManager.syncShopifyDiscounts([]);
      if (global.CartUtils) await global.CartUtils.refreshCart();
      return;
    }

    if (global.CartTimer && global.CartTimer.isExpired(data.time_applied)) {
      // Expired lock — silently unlock everything, then refresh cart
      console.log('[CartPoints] Expired lock found, cleaning up silently');
      var unlockOps = [];
      if (data.coupon_codes && data.coupon_codes.length) {
        unlockOps.push(global.CartLockManager.unlockCoupons(data.coupon_codes));
      }
      if (data.points) {
        unlockOps.push(global.CartLockManager.unlockPoints());
      }
      if (unlockOps.length) await Promise.all(unlockOps);
      // Clear all Shopify discount codes since the CRM lock is expired
      if (global.CartLockManager) await global.CartLockManager.syncShopifyDiscounts([]);
      if (global.CartUtils) await global.CartUtils.refreshCart();
      return;
    }

    // Active lock — sync Shopify discounts first, then refresh cart so
    // /cart.js reflects the applied codes when recalcTotalSavings reads it.
    if (global.CartLockManager) {
      await global.CartLockManager.syncShopifyDiscounts(data.coupon_codes || []);
    }

    // Restore points state BEFORE refreshCart so the discount-row builder can
    // read CartPoints.getApplied() for the "Cash Off (N Stars)" label.
    if (data.points) {
      var tier = _tierFor(data.points);
      if (tier) {
        // Seed CartLockManager so unlockPoints sends the correct payload.
        // Derive the points coupon code from coupon_codes (starts with 'POINTS')
        // since get-member-point-voucher-timer does not return it as a separate field.
        if (global.CartLockManager) {
          global.CartLockManager.seedPointsLock(
            tier.points,
            _findPointsCouponCode(data.coupon_codes || [])
          );
        }
        _applyTierUI(tier);
      }
    }

    if (global.CartUtils) await global.CartUtils.refreshCart();

    if (global.CartCoupon && data.coupon_codes && data.coupon_codes.length) {
      global.CartCoupon.restoreFromCodes(data.coupon_codes);
    }

    // Recalculate now that points row is visible and cart data attrs are set
    if (data.points && _applied && global.CartUtils) {
      global.CartUtils.recalcTotalSavings();
    }

    if (global.CartTimer) global.CartTimer.startAt(data.time_applied);
  }

  /* ─── Boot ───────────────────────────────────────────────────────────────── */
  document.addEventListener('DOMContentLoaded', _init);
  document.addEventListener('cart:updated', _onCartUpdated);

  /* ─── Exports ────────────────────────────────────────────────────────────── */
  global.CartPoints = {
    removeApplied:  removeApplied,
    syncFromTimer:  syncFromTimer,
    applyPoints:    _applyPoints,
    update:         _setup,
    getApplied:     function () { return _applied; },
  };

})(window);
